import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress, IconButton, Link, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import { AskAiMessage } from '../services/ai';

const normalizeTableMarkdown = (markdown: string): string => {
	if (!markdown.includes('|') || markdown.includes('\n')) {
		return markdown;
	}

	const normalized = markdown
		.replace(/\|\s+\|/g, '|\n|')
		.replace(/\s+\|/g, ' |')
		.trim();

	return normalized;
};

export const AskAIChat = ({ messages, isAskAiInProgress, onClose }: { messages: AskAiMessage[], isAskAiInProgress: boolean, onClose: () => void }) => {
	const conversationEndRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		conversationEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
	}, [messages, isAskAiInProgress]);

	return (
		<Box sx={{ position: 'relative', borderBottom: 1, borderColor: 'divider' }}>
			<IconButton onClick={onClose} aria-label='Close chat' sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}>
				<CloseIcon />
			</IconButton>
			<Box sx={{ maxHeight: '60vh', overflowY: 'auto', px: 3, pt: 6, pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
				{messages.map((message, index) => (
					message.role === 'user' ? (
						<Typography
							key={index}
							sx={{
								alignSelf: 'flex-end',
								maxWidth: '80%',
								px: 2,
								py: 1,
								borderRadius: 3,
								borderBottomRightRadius: '4px',
								bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
								whiteSpace: 'pre-wrap',
								overflowWrap: 'anywhere',
							}}
						>
							{message.content}
						</Typography>
					) : (
						<Box key={index} display='flex' flexDirection='column' gap={0.5}>
							<AutoAwesomeIcon fontSize='small' color='primary' />
							<Box
								sx={(theme) => ({
									minWidth: 0,
									color: 'text.primary',
									'& p': { margin: theme.spacing(0, 0, 1.5) },
									'& ul': { margin: theme.spacing(0, 0, 1.5), paddingLeft: theme.spacing(3) },
									'& li': { marginBottom: theme.spacing(0.5) },
									'& h3': { margin: theme.spacing(1.5, 0, 1), fontSize: theme.typography.h6.fontSize },
									'& strong': { fontWeight: 700 },
									'& table': {
										width: '100%',
										borderCollapse: 'collapse',
										marginBottom: theme.spacing(1.5),
									},
									'& th, & td': {
										border: `1px solid ${theme.palette.divider}`,
										padding: theme.spacing(0.75, 1),
										verticalAlign: 'top',
										textAlign: 'left',
									},
									'& thead th': {
										backgroundColor: theme.palette.action.hover,
									},
								})}
							>
								<ReactMarkdown
									remarkPlugins={[remarkGfm]}
									components={{
										a: ({ href = '', children }) => (href.startsWith('/')
											? <Link component={RouterLink} to={href} onClick={onClose}>{children}</Link>
											: <Link href={href} target='_blank' rel='noopener noreferrer'>{children}</Link>),
									}}
								>
									{normalizeTableMarkdown(message.content)}
								</ReactMarkdown>
							</Box>
						</Box>
					)
				))}
				{isAskAiInProgress && (
					<Box display='flex' alignItems='center' gap={1.5} color='text.secondary'>
						<CircularProgress size={20} color='inherit' />
						<Typography variant='body2'>Thinking...</Typography>
					</Box>
				)}
				<div ref={conversationEndRef} />
			</Box>
		</Box>
	);
};
